import { BaseScreen } from "./BaseScreen";

import type { AuthScreenEvents } from "./components/AuthScreenEvents";

export class OtpScreen extends BaseScreen {

    private root!: HTMLElement;

    private resendTimer: number | null = null;

    constructor(

        private readonly email: string,

        private readonly events: AuthScreenEvents

    ) {

        super();

    }

    public override mount(): void {

        super.mount();

        this.root = this.buildRoot();

        requestAnimationFrame(() => {

            this.inputs()[0]?.focus();

        });

    }

    public override update(): void {

        super.update();

        const root = this.buildRoot();

        this.root.replaceWith(root);

        this.root = root;

        requestAnimationFrame(() => {

            this.inputs()[0]?.focus();

        });

    }

    public override unmount(): void {

        if (this.resendTimer !== null) {

            window.clearInterval(this.resendTimer);

        }

        super.unmount();

    }

    public getRoot(): HTMLElement {

        return this.root;

    }

    // =====================================================
    // BUILD
    // =====================================================

    private buildRoot(): HTMLElement {

        const wrapper = document.createElement("div");

        wrapper.className = "auth-container";

        const card = document.createElement("div");

        card.className = "auth-card";

        wrapper.appendChild(card);

        const logo = document.createElement("img");

        logo.src = "/icons/logo.png";

        logo.className = "auth-logo";

        card.appendChild(logo);

        const title = document.createElement("div");

        title.className = "auth-welcome";

        title.innerHTML =
            `Ми надіслали код на<br><b>${this.email}</b>`;

        card.appendChild(title);

        const inputsContainer =
            document.createElement("div");

        inputsContainer.className =
            "otp-inputs";

        card.appendChild(inputsContainer);

        for (let i = 0; i < 4; i++) {

            const input =
                document.createElement("input");

            input.className = "otp";

            input.maxLength = 1;

            input.inputMode = "numeric";

            input.autocomplete = "one-time-code";

            input.addEventListener(
                "input",
                this.handleInput
            );

            input.addEventListener(
                "keydown",
                this.handleKeyDown
            );

            input.addEventListener(
                "paste",
                this.handlePaste
            );

            inputsContainer.appendChild(input);

        }

        const submit =
            document.createElement("button");

        submit.id = "otp-submit-btn";

        submit.textContent =
            "Підтвердити";

        submit.addEventListener(
            "click",
            () => void this.submit()
        );

        card.appendChild(submit);

        const resendWrapper =
            document.createElement("div");

        resendWrapper.style.marginTop =
            "16px";

        const resend =
            document.createElement("button");

        resend.id =
            "otp-resend-btn";

        resend.textContent =
            "Надіслати код ще раз";

        resend.addEventListener(
            "click",
            () => void this.resend()
        );

        resendWrapper.appendChild(
            resend
        );

        card.appendChild(
            resendWrapper
        );

        const error =
            document.createElement("div");

        error.id = "otp-error";

        error.className =
            "auth-error";

        card.appendChild(error);

        return wrapper;

    }

    // =====================================================
    // HELPERS
    // =====================================================

    private inputs(): HTMLInputElement[] {

        return Array.from(

            this.root.querySelectorAll<HTMLInputElement>(".otp")

        );

    }

    private submitButton(): HTMLButtonElement | null {

        return this.root.querySelector("#otp-submit-btn");

    }

    private resendButton(): HTMLButtonElement | null {

        return this.root.querySelector("#otp-resend-btn");

    }

    private error(): HTMLElement | null {

        return this.root.querySelector("#otp-error");

    }

    private setBusy(
        busy: boolean
    ): void {

        const submit = this.submitButton();
        const resend = this.resendButton();

        submit && (submit.disabled = busy);
        resend && (resend.disabled = busy);

        this.inputs().forEach(
            input => input.disabled = busy
        );

        if (submit) {
            submit.textContent =
                busy
                    ? "Перевіряємо..."
                    : "Підтвердити";
        }
    }    

    private otp(): string {

        return this.inputs()

            .map(input => input.value)

            .join("");

    }

    // =====================================================
    // INPUT HANDLERS
    // =====================================================

    private handleInput = (
        e: Event
    ): void => {

        const input =
            e.target as HTMLInputElement;

        const value =
            input.value.replace(/\D/g, "");

        input.value = value;

        input.classList.toggle(
            "filled",
            value.length > 0
        );

        const inputs =
            this.inputs();

        const index =
            inputs.indexOf(input);

        if (

            value &&
            index < inputs.length - 1

        ) {

            inputs[index + 1].focus();

        }

    };

    private handleKeyDown = (
        e: KeyboardEvent
    ): void => {

        const input =
            e.target as HTMLInputElement;

        const inputs =
            this.inputs();

        const index =
            inputs.indexOf(input);

        if (

            e.key === "Backspace" &&
            input.value === "" &&
            index > 0

        ) {

            inputs[index - 1].focus();

        }

        if (e.key === "Enter") {

            void this.submit();

        }

    };

    private handlePaste = (
        e: ClipboardEvent
    ): void => {

        e.preventDefault();

        const digits =

            (e.clipboardData?.getData("text") ?? "")

                .replace(/\D/g, "")

                .slice(0, 4);

        if (!digits) {

            return;

        }

        const inputs =
            this.inputs();

        digits.split("").forEach(

            (digit, index) => {

                if (!inputs[index]) {

                    return;

                }

                inputs[index].value = digit;

                inputs[index].classList.add(
                    "filled"
                );

            }

        );

        inputs[
            Math.min(
                digits.length - 1,
                inputs.length - 1
            )
        ]?.focus();

    };

        // =====================================================
    // ACTIONS
    // =====================================================

    private async submit(): Promise<void> {

        const error = this.error();

        if (error) {
            error.textContent = "";
        }

        const otp = this.otp();

        if (otp.length !== 4) {

            if (error) {
                error.textContent = "Введіть 4 цифри";
            }

            return;

        }

        this.setBusy(true);

        try {

            await new Promise(resolve => setTimeout(resolve, 0));

            await this.events.submitOtp(

                this.email,

                otp

            );

        } catch {

            if (error) {
                error.textContent = "Невірний код";
            }

            this.setBusy(false);

        }

    }

    private async resend(): Promise<void> {

        const button = this.resendButton();

        if (!button) {
            return;
        }

        try {

            await this.events.resendOtp(

                this.email

            );

            button.disabled = true;

            button.textContent = "Надіслано";

            let seconds = 60;

            this.resendTimer = window.setInterval(() => {

                seconds--;

                if (button.isConnected) {

                    button.textContent =
                        `Повторити через ${seconds}s`;

                }

                if (seconds <= 0) {

                    if (this.resendTimer !== null) {

                        window.clearInterval(
                            this.resendTimer
                        );

                        this.resendTimer = null;

                    }

                    if (button.isConnected) {

                        button.disabled = false;

                        button.textContent =
                            "Надіслати код ще раз";

                    }

                }

            }, 1000);

        } catch (e: any) {

            if (e?.status === 429) {

                alert(
                    "Забагато спроб. Спробуйте пізніше"
                );

                return;

            }

            alert(
                "Помилка повторної відправки"
            );

        }

    }

}